"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { CONTROL_HEIGHT_CLASSES } from "@/lib/design-system/control-density";
import { cn } from "@/lib/utils";
import { useFieldControl } from "@/components/ui/field-control";
import { EmailSuggestion, useEmailSuggestion } from "@/components/ui/email-suggestion";
import { notePointerFocus, selectUnlessPointerFocus } from "@/lib/keyboard/select-on-keyboard-focus";

const inputVariants = cva(
  "file:text-foreground placeholder:text-fg-subtle selection:bg-action-fill selection:text-action-fill-foreground w-full min-w-0 border border-input bg-card px-3 text-base rounded-field transition-[color,box-shadow] outline-none max-md:text-base file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-subtle md:text-sm focus-visible:border-ring focus-visible:ring-[2px] focus-visible:ring-ring/20 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "",
        mono: "tabular-nums",
        filled: "border-transparent bg-record-fill hover:bg-surface-2",
        ghost: "border-transparent bg-transparent hover:border-input hover:bg-background focus-visible:bg-background",
      },
      size: {
        default: `${CONTROL_HEIGHT_CLASSES.default} py-1`,
        sm: `${CONTROL_HEIGHT_CLASSES.sm} py-1`,
        compact: `${CONTROL_HEIGHT_CLASSES.compact} py-0.5 text-base md:text-sm`,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type InputProps = Omit<React.ComponentProps<"input">, "size"> &
  VariantProps<typeof inputVariants> & { suggestEmail?: boolean };

type NumericHandlers = Pick<React.ComponentProps<"input">, "onWheel" | "onFocus" | "onPointerDown">;

function numericHandlers(isNumeric: boolean, { onWheel, onFocus, onPointerDown }: NumericHandlers): NumericHandlers {
  if (!isNumeric) return { onWheel, onFocus, onPointerDown };
  return {
    onWheel: (event) => {
      event.currentTarget.blur();
      onWheel?.(event);
    },
    onPointerDown: (event) => {
      notePointerFocus(event.currentTarget);
      onPointerDown?.(event);
    },
    onFocus: (event) => {
      selectUnlessPointerFocus(event.currentTarget);
      onFocus?.(event);
    },
  };
}

function Input(inputProps: InputProps) {
  const {
    className,
    type,
    variant,
    size,
    suggestEmail = true,
    onWheel,
    onFocus,
    onPointerDown,
    onChange,
    ref,
    ...props
  } = useFieldControl(inputProps);
  const email = useEmailSuggestion({
    enabled: suggestEmail && type === "email",
    value: props.value,
    defaultValue: props.defaultValue,
    describedBy: props["aria-describedby"],
    onChange,
    ref,
  });

  return (
    <>
      <input
        type={type}
        data-slot="input"
        className={cn(inputVariants({ variant, size, className }))}
        {...numericHandlers(type === "number", { onWheel, onFocus, onPointerDown })}
        {...props}
        {...email.inputProps}
      />
      {email.suggestion ? (
        <EmailSuggestion
          id={email.suggestionId}
          suggestion={email.suggestion}
          onApply={email.apply}
        />
      ) : null}
    </>
  );
}

export { Input, inputVariants };
export type { InputProps };
